import { useState, useMemo } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { useModals } from '../context/ModalContext';
import { brokerBadgeClass, brokerLabel } from '../utils/broker';
import toast from 'react-hot-toast';

function DepositsPage() {
  const { deposits, isLoading, deleteDeposit } = usePortfolio();
  const { openDeposit } = useModals();
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const sortedDeposits = useMemo(() => {
    return [...(deposits || [])].sort((a, b) => {
      const aVal = a[sortKey] || '';
      const bVal = b[sortKey] || '';
      if (sortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [deposits, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    const success = await deleteDeposit(id);
    if (success) {
      toast.success('Entry deleted');
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Compact variant for the cramped 3-up summary cards (£20K, £1.5K, £950).
  const formatCurrencyCompact = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  // Calculate totals
  const { totalDeposits, totalWithdrawals, net } = useMemo(() => {
    const deps = (deposits || []).filter(d => d.type === 'DEPOSIT');
    const withs = (deposits || []).filter(d => d.type === 'WITHDRAWAL');
    const totalDeposits = deps.reduce((sum, d) => sum + (d.amount_gbp || 0), 0);
    const totalWithdrawals = withs.reduce((sum, d) => sum + (d.amount_gbp || 0), 0);
    return { totalDeposits, totalWithdrawals, net: totalDeposits - totalWithdrawals };
  }, [deposits]);

  if (isLoading && !deposits?.length) {
    return <div className="loading-screen"><div className="loading-spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Deposits & Withdrawals</h1>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid dashboard-grid-3 is-kpi" style={{ marginBottom: '1.5rem' }}>
        <div className="summary-card green">
          <div className="summary-card-label">Deposits</div>
          <div className="summary-card-value" title={formatCurrency(totalDeposits)}>
            {formatCurrencyCompact(totalDeposits)}
          </div>
          <div className="summary-card-sub">Cash in</div>
        </div>
        <div className="summary-card red">
          <div className="summary-card-label">Withdrawals</div>
          <div className="summary-card-value" title={formatCurrency(totalWithdrawals)}>
            {formatCurrencyCompact(totalWithdrawals)}
          </div>
          <div className="summary-card-sub">Cash out</div>
        </div>
        <div className="summary-card blue">
          <div className="summary-card-label">Net Capital</div>
          <div className="summary-card-value" title={formatCurrency(net)}>
            {formatCurrencyCompact(net)}
          </div>
          <div className="summary-card-sub">Net flow</div>
        </div>
      </div>

      {/* Table */}
      <div className="data-table-container">
        <div className="data-table-header">
          <span className="data-table-title">Deposits & Withdrawals</span>
          <span className="data-table-badge">{deposits?.length || 0} entries</span>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')}>Date {sortKey === 'date' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th onClick={() => handleSort('broker')}>Broker {sortKey === 'broker' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th className="center" onClick={() => handleSort('type')}>Type {sortKey === 'type' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th className="num" onClick={() => handleSort('amount_gbp')}>Amount (GBP) {sortKey === 'amount_gbp' && (sortDir === 'asc' ? '↑' : '↓')}</th>
                <th className="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDeposits.map(deposit => (
                <tr key={deposit.id}>
                  <td>{deposit.date}</td>
                  <td>
                    <span className={brokerBadgeClass(deposit.broker)}>
                      {brokerLabel(deposit.broker)}
                    </span>
                  </td>
                  <td className="center">
                    <span style={{ 
                      color: deposit.type === 'DEPOSIT' ? 'var(--green)' : 'var(--red)',
                      fontWeight: 600,
                    }}>
                      {deposit.type}
                    </span>
                  </td>
                  <td className={`num ${deposit.type === 'DEPOSIT' ? 'pos' : 'neg'}`}>
                    {formatCurrency(deposit.amount_gbp)}
                  </td>
                  <td className="center">
                    <div className="table-actions">
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => openDeposit(deposit)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(deposit.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DepositsPage;
